import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { DocumentPreviewModal } from "../components/ui/document-preview-modal";
import AdminSidebar from "../components/layout/AdminSidebar";
import { 
  FileText, 
  Search,
  Upload,
  Download,
  Trash2,
  Filter,
  ArrowLeft,
  File,
  FileType,
  BookOpen,
  History,
  Eye,
  EyeOff,
  Plus,
  Tag,
  Calendar,
  RefreshCw,
  MoreVertical,
  FileImage,
  Bot,
  Loader2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";

const CATEGORIES = [
  { value: "ccrs", label: "CC&Rs" },
  { value: "bylaws", label: "Bylaws" },
  { value: "design_guidelines", label: "Design Guidelines" },
  { value: "policies", label: "Policies" },
  { value: "budgets", label: "Budgets" },
  { value: "minutes", label: "Meeting Minutes" },
  { value: "maps", label: "Maps" },
  { value: "metro_district", label: "Metro District" },
  { value: "colorado_law", label: "Colorado Law" },
  { value: "articles", label: "Articles of Incorporation" },
  { value: "supplements", label: "Supplements" },
  { value: "rules", label: "Rules & Regulations" },
  { value: "enforcement", label: "Enforcement Policies" },
  { value: "fine_schedule", label: "Fine Schedule" },
  { value: "collections", label: "Collection Policies" }
];

const DocumentLibrary = ({ isBoard = false }) => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [visibilityFilter, setVisibilityFilter] = useState("");
  
  // Upload dialog state
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadData, setUploadData] = useState({ 
    title: "", 
    category: "", 
    description: "", 
    visibility: "private",
    tags: "",
    effective_date: ""
  });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [reindexing, setReindexing] = useState(false);
  
  // Version dialog state
  const [versionOpen, setVersionOpen] = useState(false);
  const [selectedDocForVersion, setSelectedDocForVersion] = useState(null);
  const [versionFile, setVersionFile] = useState(null);
  const [versionDescription, setVersionDescription] = useState("");
  
  // Version history dialog
  const [historyOpen, setHistoryOpen] = useState(false);
  const [versionHistory, setVersionHistory] = useState([]);
  const [selectedDocForHistory, setSelectedDocForHistory] = useState(null);

  // Preview modal state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);

  const fetchDocuments = async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append("search", searchQuery);
      if (categoryFilter && categoryFilter !== "all") params.append("category", categoryFilter);
      if (visibilityFilter && visibilityFilter !== "all") params.append("visibility", visibilityFilter);
      
      const response = await axios.get(`${API}/documents?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
    } catch (error) {
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [token, searchQuery, categoryFilter, visibilityFilter]);

  const handleReindexAll = async () => {
    setReindexing(true);
    try {
      const res = await axios.post(`${API}/documents/admin/reingest-all`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 300000
      });
      const s = res.data.summary;
      toast.success(`Re-indexed ${s.success} of ${s.total} documents (${s.total_chunks} chunks)`);
      fetchDocuments();
    } catch (err) {
      toast.error("Re-indexing failed or timed out. Check backend logs.");
    } finally {
      setReindexing(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadData.title || !uploadData.category || !uploadFile) {
      toast.error("Please fill in all required fields");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("title", uploadData.title);
      formData.append("category", uploadData.category);
      formData.append("visibility", uploadData.visibility);
      if (uploadData.description) formData.append("description", uploadData.description);
      if (uploadData.tags) formData.append("tags", uploadData.tags);
      if (uploadData.effective_date) formData.append("effective_date", uploadData.effective_date);

      await axios.post(`${API}/documents`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      toast.success("Document uploaded and AI-indexed automatically");
      setUploadOpen(false);
      setUploadData({ title: "", category: "", description: "", visibility: "private", tags: "", effective_date: "" });
      setUploadFile(null);
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleUploadNewVersion = async () => {
    if (!selectedDocForVersion || !versionFile) {
      toast.error("Please select a file");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", versionFile);
      if (versionDescription) formData.append("description", versionDescription);

      const response = await axios.post(
        `${API}/documents/${selectedDocForVersion.document_id}/new-version`,
        formData,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        }
      );

      toast.success(`Version ${response.data.version_number} uploaded successfully`);
      setVersionOpen(false);
      setSelectedDocForVersion(null);
      setVersionFile(null);
      setVersionDescription("");
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Version upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleViewVersionHistory = async (doc) => {
    setSelectedDocForHistory(doc);
    try {
      const response = await axios.get(`${API}/documents/${doc.document_id}/versions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVersionHistory(response.data.versions);
      setHistoryOpen(true);
    } catch (error) {
      toast.error("Failed to load version history");
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(`${API}/documents/${doc.document_id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.file_name || 'document');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Download started");
    } catch (error) {
      toast.error("Failed to download document");
    }
  };

  const handleToggleVisibility = async (doc) => {
    const newVisibility = doc.visibility === "public" ? "private" : "public";
    try {
      await axios.put(`${API}/documents/${doc.document_id}`, 
        { visibility: newVisibility },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Document is now ${newVisibility}`);
      fetchDocuments();
    } catch (error) {
      toast.error("Failed to update visibility");
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm("Are you sure you want to delete this document?")) return;

    try {
      await axios.delete(`${API}/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Document deleted");
      fetchDocuments();
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case "ccrs":
      case "bylaws":
        return <BookOpen className="w-5 h-5" />;
      case "maps":
        return <FileType className="w-5 h-5" />;
      default:
        return <File className="w-5 h-5" />;
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      ccrs: "bg-primary/10 text-primary",
      bylaws: "bg-chart-2/10 text-chart-2",
      design_guidelines: "bg-secondary/10 text-secondary",
      policies: "bg-chart-4/10 text-chart-4",
      budgets: "bg-chart-1/10 text-chart-1",
      minutes: "bg-muted text-muted-foreground",
      maps: "bg-chart-3/10 text-chart-3",
      metro_district: "bg-destructive/10 text-destructive",
      colorado_law: "bg-primary/10 text-primary"
    };
    return colors[category] || "bg-muted text-muted-foreground";
  };

  // Preview document handler
  const handlePreview = (doc) => {
    const previewDoc = {
      document_id: doc.document_id,
      filename: doc.file_name,
      title: doc.title,
      content_type: doc.content_type || getContentTypeFromFilename(doc.file_name),
      file_url: `${API}/documents/${doc.document_id}/download`,
    };
    setPreviewDocument(previewDoc);
    setPreviewOpen(true);
  };

  // Get content type from filename
  const getContentTypeFromFilename = (filename) => {
    if (!filename) return "application/octet-stream";
    const ext = filename.toLowerCase().split(".").pop();
    const types = {
      pdf: "application/pdf",
      png: "image/png",
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      gif: "image/gif",
      webp: "image/webp",
      txt: "text/plain",
      md: "text/markdown",
      csv: "text/csv",
      json: "application/json",
    };
    return types[ext] || "application/octet-stream";
  };

  // Check if document can be previewed
  const canPreview = (doc) => {
    const filename = doc.file_name?.toLowerCase() || "";
    return (
      filename.endsWith(".pdf") ||
      filename.endsWith(".png") ||
      filename.endsWith(".jpg") ||
      filename.endsWith(".jpeg") ||
      filename.endsWith(".gif") ||
      filename.endsWith(".webp") ||
      filename.endsWith(".txt") ||
      filename.endsWith(".md")
    );
  };

  const content = (
    <div className={isBoard ? "p-6 lg:p-8 max-w-7xl mx-auto" : "min-h-screen bg-background"}>
      {/* Header - only for non-board */}
      {!isBoard && (
        <header className="border-b bg-card/95 backdrop-blur-sm sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/owner")}
                className="text-muted-foreground hover:text-foreground transition-colors duration-200"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <span className="font-serif text-lg font-semibold block">Document Library</span>
                  <span className="text-xs text-muted-foreground">Access governing documents and records</span>
                </div>
              </div>
            </div>
          </div>
        </header>
      )}

      {/* Page Title for Board */}
      {isBoard && (
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <FileText className="w-5 h-5 text-primary" />
          </div>
          <div>
            <span className="font-serif text-lg font-semibold block">Document Library</span>
            <span className="text-xs text-muted-foreground">Documents are auto-indexed for AI search</span>
          </div>
        </div>
      )}

      <div className={isBoard ? "" : "max-w-7xl mx-auto px-4 md:px-8 py-8"}>
        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search documents by title, description, or tags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 focus:ring-primary/20"
              data-testid="search-documents-input"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-full sm:w-[200px]" data-testid="category-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {CATEGORIES.map((cat) => (
                <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {isBoard && (
            <>
              <Select value={visibilityFilter} onValueChange={setVisibilityFilter}>
                <SelectTrigger className="w-full sm:w-[150px]" data-testid="visibility-filter">
                  <SelectValue placeholder="Visibility" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="public">Public</SelectItem>
                  <SelectItem value="private">Private</SelectItem>
                </SelectContent>
              </Select>

              <Button variant="outline" onClick={handleReindexAll} disabled={reindexing} data-testid="reindex-all-btn">
                {reindexing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bot className="w-4 h-4 mr-2" />}
                {reindexing ? "Indexing..." : "Re-Index All for AI"}
              </Button>
              
              <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="upload-document-btn" className="bg-primary text-primary-foreground shadow-button hover:shadow-button-hover transition-all duration-300">
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Document
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-serif">Upload Document</DialogTitle>
                    <DialogDescription>Add a new document to the library</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="title">Title *</Label>
                      <Input
                        id="title"
                        value={uploadData.title}
                        onChange={(e) => setUploadData({ ...uploadData, title: e.target.value })}
                        placeholder="Document title"
                        data-testid="upload-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="category">Category *</Label>
                      <Select
                        value={uploadData.category}
                        onValueChange={(v) => setUploadData({ ...uploadData, category: v })}
                      >
                        <SelectTrigger data-testid="upload-category-select">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {CATEGORIES.map((cat) => (
                            <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        value={uploadData.description}
                        onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                        placeholder="Brief description of the document"
                        data-testid="upload-description-input"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label>Public Visibility</Label>
                        <p className="text-xs text-muted-foreground">Make visible to all owners</p>
                      </div>
                      <Switch
                        checked={uploadData.visibility === "public"}
                        onCheckedChange={(checked) => setUploadData({ ...uploadData, visibility: checked ? "public" : "private" })}
                        data-testid="upload-visibility-switch"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="tags">Tags</Label>
                      <Input
                        id="tags"
                        value={uploadData.tags}
                        onChange={(e) => setUploadData({ ...uploadData, tags: e.target.value })}
                        placeholder="Comma-separated tags (e.g., amendment, 2024, parking)"
                        data-testid="upload-tags-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="effective_date">Effective Date</Label>
                      <Input
                        id="effective_date"
                        type="date"
                        value={uploadData.effective_date}
                        onChange={(e) => setUploadData({ ...uploadData, effective_date: e.target.value })}
                        data-testid="upload-date-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="file">File *</Label>
                      <Input
                        id="file"
                        type="file"
                        onChange={(e) => setUploadFile(e.target.files[0])}
                        accept=".pdf,.doc,.docx,.txt,.xls,.xlsx"
                        data-testid="upload-file-input"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setUploadOpen(false)}>Cancel</Button>
                    <Button onClick={handleUpload} disabled={uploading} data-testid="upload-submit-btn">
                      {uploading ? "Uploading..." : "Upload"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>

        {/* Documents Grid */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading documents...</div>
        ) : documents.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No documents found</p>
              {isBoard && (
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setUploadOpen(true)}
                >
                  Upload your first document
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {documents.map((doc) => (
              <Card 
                key={doc.document_id} 
                data-testid={`document-${doc.document_id}`} 
                className="hover:shadow-floating transition-shadow cursor-pointer group"
                onClick={() => canPreview(doc) ? handlePreview(doc) : handleDownload(doc)}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${getCategoryColor(doc.category)}`}>
                      {getCategoryIcon(doc.category)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-foreground truncate">{doc.title}</h3>
                        {doc.visibility === "public" ? (
                          <Eye className="w-4 h-4 text-green-500" title="Public" />
                        ) : (
                          <EyeOff className="w-4 h-4 text-muted-foreground" title="Private" />
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="secondary" className="text-xs">
                          {CATEGORIES.find(c => c.value === doc.category)?.label || doc.category}
                        </Badge>
                        {doc.version_number > 1 && (
                          <Badge variant="outline" className="text-xs">
                            v{doc.version_number}
                          </Badge>
                        )}
                        {doc.total_versions > 1 && (
                          <Badge variant="outline" className="text-xs text-muted-foreground">
                            {doc.total_versions} versions
                          </Badge>
                        )}
                        {doc.metadata?.ai_status === "indexed" && (
                          <Badge className="text-[10px] bg-green-100 text-green-700 border-green-200">
                            AI Indexed ({doc.metadata.ai_chunks} chunks)
                          </Badge>
                        )}
                        {doc.metadata?.ai_status === "no_text" && (
                          <Badge className="text-[10px] bg-amber-100 text-amber-700 border-amber-200">
                            No Extractable Text
                          </Badge>
                        )}
                        {(!doc.metadata?.ai_status || doc.metadata?.ai_status === "NONE") && (
                          <Badge className="text-[10px] bg-gray-100 text-gray-500">
                            Not AI Indexed
                          </Badge>
                        )}
                      </div>
                      {doc.description && (
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{doc.description}</p>
                      )}
                      {doc.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {doc.tags.slice(0, 3).map((tag, i) => (
                            <span key={i} className="text-xs bg-muted px-2 py-0.5 rounded-full">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Hover actions */}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <span className="text-xs text-muted-foreground truncate max-w-[150px]">
                      {doc.file_name}
                    </span>
                    
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                      {canPreview(doc) && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handlePreview(doc)}
                          title="Preview"
                          data-testid={`preview-doc-${doc.document_id}`}
                        >
                          <FileImage className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDownload(doc)}
                        title="Download"
                        data-testid={`download-doc-${doc.document_id}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      
                      {isBoard && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewVersionHistory(doc)}>
                              <History className="w-4 h-4 mr-2" /> Version History
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => {
                              setSelectedDocForVersion(doc);
                              setVersionOpen(true);
                            }}>
                              <RefreshCw className="w-4 h-4 mr-2" /> Upload New Version
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleToggleVisibility(doc)}>
                              {doc.visibility === "public" ? (
                                <><EyeOff className="w-4 h-4 mr-2" /> Make Private</>
                              ) : (
                                <><Eye className="w-4 h-4 mr-2" /> Make Public</>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              className="text-destructive"
                              onClick={() => handleDelete(doc.document_id)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Upload New Version Dialog */}
        <Dialog open={versionOpen} onOpenChange={setVersionOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-serif">Upload New Version</DialogTitle>
              <DialogDescription>
                {selectedDocForVersion && (
                  <>Upload a new version of "<strong>{selectedDocForVersion.title}</strong>"</>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="version-description">Description of Changes</Label>
                <Textarea
                  id="version-description"
                  value={versionDescription}
                  onChange={(e) => setVersionDescription(e.target.value)}
                  placeholder="What changed in this version?"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="version-file">New File *</Label>
                <Input
                  id="version-file"
                  type="file"
                  onChange={(e) => setVersionFile(e.target.files[0])}
                  accept=".pdf,.doc,.docx,.txt,.xls,.xlsx"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setVersionOpen(false)}>Cancel</Button>
              <Button onClick={handleUploadNewVersion} disabled={uploading || !versionFile}>
                {uploading ? "Uploading..." : "Upload Version"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Version History Dialog */}
        <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-serif">Version History</DialogTitle>
              <DialogDescription>
                {selectedDocForHistory && (
                  <>All versions of "<strong>{selectedDocForHistory.title}</strong>"</>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-4 max-h-[400px] overflow-y-auto">
              {versionHistory.map((version) => (
                <div 
                  key={version.document_id} 
                  className={`p-3 border rounded-lg ${version.is_current_version ? 'border-primary bg-primary/5' : ''}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant={version.is_current_version ? "default" : "outline"}>
                        v{version.version_number}
                      </Badge>
                      {version.is_current_version && (
                        <span className="text-xs text-primary font-medium">Current</span>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDownload(version)}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    {version.file_name}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Uploaded: {new Date(version.created_at).toLocaleDateString()}
                  </p>
                  {version.description && (
                    <p className="text-sm mt-2">{version.description}</p>
                  )}
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setHistoryOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Document Preview Modal */}
      <DocumentPreviewModal
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewDocument(null);
        }}
        document={previewDocument}
        token={token}
      />
    </div>
  );

  if (isBoard) {
    return <AdminSidebar>{content}</AdminSidebar>;
  }
  return content;
};

export default DocumentLibrary;
